import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    PiClipboardTextBold,
    PiEyeBold,
    PiFileBold,
    PiFilePdfBold,
    PiFileXlsBold,
    PiImageBold,
    PiMagnifyingGlassBold,
    PiShoppingCartBold,
    PiUploadSimpleBold,
} from 'react-icons/pi';
import { Button, PageHeader, Text, LoadingState, cn } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import ShipmentCheckByContainerView from '@v2/features/shipment-check/ShipmentCheckByContainerView';

type PathKey = 'documents' | 'purchase_order' | 'check' | 'receive_cd';
type Props = { title: string; pathKey: PathKey };

const V1_KEY: Record<PathKey, string> = {
    documents: 'documents_v1',
    purchase_order: 'purchase_order_v1',
    check: 'check_v1',
    receive_cd: 'receive_cd_v1',
};

const formatUSD = (n: number) =>
    n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export default function ShipmentOperationalPage({ title, pathKey }: Props) {
    const { id } = useParams<{ id: string }>();

    const { data: links } = useQuery({
        queryKey: ['shipment-links', id],
        queryFn: async () => {
            const { data } = await api.get<{ v2: Record<string, string> } & Record<string, string>>(
                `/shipments/${id}/operational-links`,
            );
            return data;
        },
    });

    const v1Url = links?.[V1_KEY[pathKey]];

    return (
        <div className="flex flex-col gap-4">
            <PageHeader
                title={title}
                breadcrumb={[
                    { name: 'Embarques', href: '/shipments' },
                    { name: `#${id}`, href: `/shipments/${id}` },
                    { name: title },
                ]}
            >
                {v1Url && (
                    <a href={v1Url} target="_blank" rel="noreferrer">
                        <Button as="span" size="sm" variant="outline">
                            Editar no painel completo (v1)
                        </Button>
                    </a>
                )}
            </PageHeader>

            {pathKey === 'documents' && <DocumentsSection shipmentId={id!} />}
            {pathKey === 'purchase_order' && <PurchaseOrderSection shipmentId={id!} />}
            {pathKey === 'check' && <ShipmentCheckByContainerView shipmentId={id!} />}
            {pathKey === 'receive_cd' && <ReceiveCdSection shipmentId={id!} />}
        </div>
    );
}

/* ============================== DOCUMENTS ============================== */

type DocsResponse = {
    shipment: { id: number; order_number: string };
    purchase_order: { label?: string; path?: string; url: string }[];
    uploads_by_task: { task_title: string; files: { path: string; url: string }[] }[];
    checks: { label: string; download_url: string }[];
    has_purchase_order: boolean;
};

type DocItem = {
    id: string;
    label: string;
    url: string;
    group: string;
    kind: DocKind;
};

type DocKind = 'pdf' | 'image' | 'sheet' | 'file';

function DocumentsSection({ shipmentId }: { shipmentId: string }) {
    const { t } = useI18n();
    const [query, setQuery] = useState('');
    const [kind, setKind] = useState<DocKind | 'all'>('all');
    const [preview, setPreview] = useState<DocItem | null>(null);

    const { data, isLoading } = useQuery({
        queryKey: ['shipment-documents', shipmentId],
        queryFn: async () => {
            const { data } = await api.get<DocsResponse>(`/shipments/${shipmentId}/documents`);
            return data;
        },
    });

    const groups = useMemo(() => {
        if (!data) return null;
        const needle = query.trim().toLowerCase();
        const match = (item: DocItem) =>
            (kind === 'all' || item.kind === kind) &&
            (needle === '' || `${item.label} ${item.group}`.toLowerCase().includes(needle));

        const po = data.purchase_order
            .map((p, i) => toDocItem(`po-${i}`, p.label ?? fileName(p.path) ?? `PO ${i + 1}`, p.url, t('v2.shipment_documents.section_po')))
            .filter(match);
        const tasks = data.uploads_by_task
            .map((g) => ({
                title: g.task_title,
                files: g.files
                    .map((f, j) => toDocItem(`task-${g.task_title}-${j}`, fileName(f.path) ?? f.path, f.url, g.task_title))
                    .filter(match),
            }))
            .filter((g) => g.files.length > 0);
        const checks = data.checks
            .map((c, i) => toDocItem(`check-${i}`, c.label, c.download_url, t('v2.shipment_documents.section_checks')))
            .filter(match);

        return { po, tasks, checks };
    }, [data, kind, query, t]);

    if (isLoading) return <LoadingState variant="detail" />;
    if (!data || !groups) return null;

    const total =
        data.purchase_order.length +
        data.uploads_by_task.reduce((acc, task) => acc + task.files.length, 0) +
        data.checks.length;
    const visible = groups.po.length + groups.tasks.reduce((acc, task) => acc + task.files.length, 0) + groups.checks.length;
    const filtering = query.trim() !== '' || kind !== 'all';

    return (
        <div className="space-y-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Text className="text-sm text-gray-600 dark:text-gray-400">
                    {total === 0
                        ? t('v2.shipment_documents.none')
                        : t('v2.shipment_documents.count', { count: filtering ? visible : total })}
                </Text>
                <div className="relative w-full sm:max-w-xs">
                    <PiMagnifyingGlassBold className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t('v2.shipment_documents.search')}
                        className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-800 outline-none ring-primary/30 placeholder:text-gray-400 focus:ring-2 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    />
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                {(['all', 'pdf', 'image', 'sheet', 'file'] as const).map((key) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setKind(key)}
                        className={cn(
                            'rounded-full border px-3 py-1 text-xs font-medium transition',
                            kind === key
                                ? 'border-gray-900 bg-gray-900 text-white dark:border-white dark:bg-white dark:text-gray-900'
                                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300',
                        )}
                    >
                        {t(`v2.shipment_documents.${key === 'all' ? 'filter_all' : `kind_${key}`}`)}
                    </button>
                ))}
            </div>

            {filtering && visible === 0 ? (
                <p className="text-sm text-gray-500">{t('v2.shipment_documents.no_results')}</p>
            ) : null}

            <DocSection
                icon={<PiShoppingCartBold className="size-4" />}
                title={t('v2.shipment_documents.section_po')}
                count={groups.po.length}
                empty={t('v2.shipment_documents.empty_po')}
                hideEmpty={filtering}
            >
                <DocGrid items={groups.po} onPreview={setPreview} previewLabel={t('v2.shipment_documents.preview')} openLabel={t('v2.shipment_documents.open')} />
            </DocSection>

            <DocSection
                icon={<PiUploadSimpleBold className="size-4" />}
                title={t('v2.shipment_documents.section_tasks')}
                count={groups.tasks.reduce((acc, task) => acc + task.files.length, 0)}
                empty={t('v2.shipment_documents.empty_tasks')}
                hideEmpty={filtering}
            >
                <div className="space-y-5">
                    {groups.tasks.map((group) => (
                        <div key={group.title} className="space-y-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                {group.title}
                            </p>
                            <DocGrid items={group.files} onPreview={setPreview} previewLabel={t('v2.shipment_documents.preview')} openLabel={t('v2.shipment_documents.open')} />
                        </div>
                    ))}
                </div>
            </DocSection>

            <DocSection
                icon={<PiClipboardTextBold className="size-4" />}
                title={t('v2.shipment_documents.section_checks')}
                count={groups.checks.length}
                empty={t('v2.shipment_documents.empty_checks')}
                hideEmpty={filtering}
            >
                <DocGrid items={groups.checks} onPreview={setPreview} previewLabel={t('v2.shipment_documents.preview')} openLabel={t('v2.shipment_documents.open')} />
            </DocSection>

            <DocumentPreviewModal
                item={preview}
                onClose={() => setPreview(null)}
                openLabel={t('v2.shipment_documents.open')}
                closeLabel={t('v2.shipment_documents.close')}
                unavailable={t('v2.shipment_documents.preview_unavailable')}
            />
        </div>
    );
}

function DocSection({
    icon,
    title,
    count,
    empty,
    hideEmpty,
    children,
}: {
    icon: React.ReactNode;
    title: string;
    count: number;
    empty: string;
    hideEmpty?: boolean;
    children: React.ReactNode;
}) {
    if (count === 0 && hideEmpty) return null;

    return (
        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="mb-3 flex items-center gap-2">
                <span className="inline-flex size-7 items-center justify-center rounded-lg bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                    {icon}
                </span>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{title}</h3>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                    {count}
                </span>
            </div>
            {count === 0 ? <p className="text-sm text-gray-500">{empty}</p> : children}
        </section>
    );
}

function DocGrid({
    items,
    onPreview,
    previewLabel,
    openLabel,
}: {
    items: DocItem[];
    onPreview: (item: DocItem) => void;
    previewLabel: string;
    openLabel: string;
}) {
    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {items.map((item) => (
                <DocumentCard key={item.id} item={item} onPreview={onPreview} previewLabel={previewLabel} openLabel={openLabel} />
            ))}
        </div>
    );
}

function DocumentCard({
    item,
    onPreview,
    previewLabel,
    openLabel,
}: {
    item: DocItem;
    onPreview: (item: DocItem) => void;
    previewLabel: string;
    openLabel: string;
}) {
    const tone = kindTone(item.kind);

    return (
        <article className="group flex overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:border-gray-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800">
            <button
                type="button"
                onClick={() => onPreview(item)}
                className={cn('flex w-24 shrink-0 flex-col items-center justify-center gap-1', tone.bg)}
            >
                {item.kind === 'image' ? (
                    <img src={item.url} alt="" className="size-full object-cover" loading="lazy" />
                ) : (
                    <>
                        <KindIcon kind={item.kind} className={cn('size-8', tone.icon)} />
                        <span className={cn('text-[10px] font-bold uppercase tracking-wide', tone.icon)}>{item.kind}</span>
                    </>
                )}
            </button>
            <div className="flex min-w-0 flex-1 flex-col gap-2 p-3">
                <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100" title={item.label}>
                        {item.label}
                    </p>
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400" title={item.group}>
                        {item.group}
                    </p>
                </div>
                <div className="mt-auto flex gap-2">
                    <button
                        type="button"
                        onClick={() => onPreview(item)}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                        <PiEyeBold className="size-3.5" />
                        {previewLabel}
                    </button>
                    <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium text-blue-600 hover:underline"
                    >
                        {openLabel}
                    </a>
                </div>
            </div>
        </article>
    );
}

function DocumentPreviewModal({
    item,
    onClose,
    openLabel,
    closeLabel,
    unavailable,
}: {
    item: DocItem | null;
    onClose: () => void;
    openLabel: string;
    closeLabel: string;
    unavailable: string;
}) {
    return (
        <V2Modal
            open={!!item}
            onClose={onClose}
            title={item?.label ?? ''}
            description={item?.group}
            size="5xl"
            footer={
                <>
                    {item && (
                        <a
                            href={item.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex h-9 items-center rounded-md border border-gray-300 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                        >
                            {openLabel}
                        </a>
                    )}
                    <Button type="button" onClick={onClose}>
                        {closeLabel}
                    </Button>
                </>
            }
        >
            {item && item.kind === 'pdf' && (
                <iframe title={item.label} src={item.url} className="h-[60vh] w-full rounded-lg border border-gray-200 bg-white dark:border-gray-700" />
            )}
            {item && item.kind === 'image' && (
                <img src={item.url} alt={item.label} className="mx-auto max-h-[68vh] w-full rounded-lg object-contain" />
            )}
            {item && item.kind !== 'pdf' && item.kind !== 'image' && (
                <div className="flex h-48 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800">
                    <KindIcon kind={item.kind} className="size-12 text-gray-400" />
                    {unavailable}
                </div>
            )}
        </V2Modal>
    );
}

function KindIcon({ kind, className }: { kind: DocKind; className?: string }) {
    if (kind === 'pdf') return <PiFilePdfBold className={className} />;
    if (kind === 'image') return <PiImageBold className={className} />;
    if (kind === 'sheet') return <PiFileXlsBold className={className} />;
    return <PiFileBold className={className} />;
}

function kindTone(kind: DocKind) {
    if (kind === 'pdf') return { bg: 'bg-rose-50 dark:bg-rose-950/40', icon: 'text-rose-600' };
    if (kind === 'image') return { bg: 'bg-sky-50 dark:bg-sky-950/40', icon: 'text-sky-600' };
    if (kind === 'sheet') return { bg: 'bg-emerald-50 dark:bg-emerald-950/40', icon: 'text-emerald-600' };
    return { bg: 'bg-gray-50 dark:bg-gray-800', icon: 'text-gray-500' };
}

function toDocItem(id: string, label: string, url: string, group: string): DocItem {
    return { id, label, url, group, kind: kindFromName(label) };
}

function kindFromName(name: string): DocKind {
    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    if (ext === 'pdf') return 'pdf';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'sheet';
    return 'file';
}

function fileName(path?: string) {
    if (!path) return null;
    const name = path.split('/').pop();
    return name && name !== '' ? name : null;
}

/* =========================== PURCHASE ORDER =========================== */

type POResponse = {
    shipment: { id: number; order_number: string };
    purchase_order: null | {
        id: number;
        code: string | null;
        status: string;
        supplier: string | null;
        supplier_name: string | null;
        import_cnpj: string | null;
        total_usd: number;
        total_cbm: number;
        containers_count: number;
        notes: string | null;
        closed_at: string | null;
        closed_by: string | null;
    };
    items: Array<{
        id: number;
        product_code: string | null;
        product_name: string;
        quantity: number;
        unit_price_usd: number;
        discount_percent: number;
        total_usd: number;
        cbm_unit: number;
        total_cbm: number;
    }>;
    orders: Array<{ id: number; order_number: string }>;
    pdfs: { lang: string; url: string }[];
    edit_v1_url: string;
};

function PurchaseOrderSection({ shipmentId }: { shipmentId: string }) {
    const { data, isLoading } = useQuery({
        queryKey: ['shipment-po', shipmentId],
        queryFn: async () => {
            const { data } = await api.get<POResponse>(`/shipments/${shipmentId}/purchase-order`);
            return data;
        },
    });

    if (isLoading) return <LoadingState variant="detail" />;
    if (!data) return null;
    const po = data.purchase_order;

    if (!po) {
        return (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                Nenhuma Purchase Order foi gerada para este embarque ainda.
                <div className="mt-2">
                    <a href={data.edit_v1_url} target="_blank" rel="noreferrer">
                        <Button as="span" size="sm">
                            Criar no painel completo
                        </Button>
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <section className="grid grid-cols-2 gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:grid-cols-4">
                <Stat label="Código" value={po.code ?? '—'} />
                <Stat label="Status" value={po.status} />
                <Stat label="Fornecedor" value={po.supplier_name ?? po.supplier ?? '—'} />
                <Stat label="CNPJ de importação" value={po.import_cnpj ?? '—'} />
                <Stat label="Total USD" value={formatUSD(po.total_usd)} />
                <Stat label="Total CBM" value={po.total_cbm.toFixed(3)} />
                <Stat label="Containers" value={String(po.containers_count)} />
                <Stat label="Fechada em" value={po.closed_at?.slice(0, 10) ?? '—'} />
            </section>

            {data.orders.length > 0 && (
                <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <h3 className="mb-3 text-sm font-semibold text-gray-800">
                        Pedidos vinculados ({data.orders.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        {data.orders.map((o) => (
                            <span
                                key={o.id}
                                className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-800"
                            >
                                {o.order_number}
                            </span>
                        ))}
                    </div>
                </section>
            )}

            {data.pdfs.length > 0 && (
                <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <h3 className="mb-3 text-sm font-semibold text-gray-800">PDFs gerados</h3>
                    <div className="flex flex-wrap gap-2">
                        {data.pdfs.map((p, i) => (
                            <a key={i} href={p.url} target="_blank" rel="noreferrer">
                                <Button as="span" size="sm" variant="outline">
                                    PDF — {p.lang}
                                </Button>
                            </a>
                        ))}
                    </div>
                </section>
            )}

            <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
                <h3 className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-800">
                    Itens ({data.items.length})
                </h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-100 text-sm">
                        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                            <tr>
                                <th className="px-3 py-2 text-left">Código</th>
                                <th className="px-3 py-2 text-left">Produto</th>
                                <th className="px-3 py-2 text-right">Qtd</th>
                                <th className="px-3 py-2 text-right">Preço un.</th>
                                <th className="px-3 py-2 text-right">Desc. %</th>
                                <th className="px-3 py-2 text-right">Total USD</th>
                                <th className="px-3 py-2 text-right">CBM</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {data.items.map((it) => (
                                <tr key={it.id}>
                                    <td className="px-3 py-2 text-gray-700">{it.product_code ?? '—'}</td>
                                    <td className="px-3 py-2 text-gray-700">{it.product_name}</td>
                                    <td className="px-3 py-2 text-right">{it.quantity}</td>
                                    <td className="px-3 py-2 text-right">{formatUSD(it.unit_price_usd)}</td>
                                    <td className="px-3 py-2 text-right">{it.discount_percent}%</td>
                                    <td className="px-3 py-2 text-right">{formatUSD(it.total_usd)}</td>
                                    <td className="px-3 py-2 text-right">{it.total_cbm.toFixed(3)}</td>
                                </tr>
                            ))}
                            {data.items.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                                        Sem itens.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {po.notes && (
                <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <h3 className="mb-2 text-sm font-semibold text-gray-800">Observações</h3>
                    <pre className="whitespace-pre-wrap text-sm text-gray-700">{po.notes}</pre>
                </section>
            )}
        </div>
    );
}

function Stat({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <div className="text-xs uppercase text-gray-500">{label}</div>
            <div className="text-sm font-semibold text-gray-800">{value}</div>
        </div>
    );
}

function Pill({
    label,
    value,
    tone,
}: {
    label: string;
    value: number;
    tone: 'gray' | 'green' | 'amber' | 'red';
}) {
    const cls = {
        gray: 'bg-gray-50 text-gray-700',
        green: 'bg-green-50 text-green-700',
        amber: 'bg-amber-50 text-amber-700',
        red: 'bg-red-50 text-red-700',
    }[tone];
    return (
        <div className={`rounded-md px-2 py-2 text-center ${cls}`}>
            <div className="text-lg font-semibold">{value}</div>
            <div className="text-xs uppercase">{label}</div>
        </div>
    );
}

/* ============================ RECEIVE CD ============================ */

type ReceiveCdResponse = {
    shipment: { id: number; order_number: string; actual_cd_date: string | null };
    items: Array<{
        id: number;
        product_code: string | null;
        product_name: string | null;
        serial_number: string | null;
        status: string;
        cd_location: string | null;
        cd_arrived_at: string | null;
    }>;
    totals: { total: number; allocated: number; pending: number };
    checks_count?: number;
    urls?: {
        check_v1: string;
        conference_v1: string;
        check_v2: string;
        conference_v2: string;
    };
    edit_v1_url: string;
};

function ReceiveCdSection({ shipmentId }: { shipmentId: string }) {
    const { data, isLoading } = useQuery({
        queryKey: ['shipment-receive-cd', shipmentId],
        queryFn: async () => {
            const { data } = await api.get<ReceiveCdResponse>(`/shipments/${shipmentId}/receive-cd`);
            return data;
        },
    });

    if (isLoading) return <LoadingState variant="detail" />;
    if (!data) return null;

    if (data.totals.total === 0) {
        return (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-950 shadow-sm">
                <p className="font-semibold">Alocação em ruas do CD (estoque serializado)</p>
                <p className="mt-2 text-amber-900/90">
                    Esta tela só exibe itens de estoque com <strong>número de série</strong> vinculados ao embarque.
                    As tarefas 39–41 (planilha Details, conferência física, romaneio) ficam em{' '}
                    <strong>Conferência</strong> e <strong>Romaneio</strong> — não aqui.
                </p>
                {(data.checks_count ?? 0) > 0 && data.urls ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                        <a
                            href={data.urls.check_v1}
                            className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700"
                        >
                            Conferência física (v1)
                        </a>
                        <a
                            href={data.urls.conference_v1}
                            className="rounded-lg border border-amber-600 bg-white px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100"
                        >
                            Romaneio (v1)
                        </a>
                        <Link
                            to={`/shipments/${shipmentId}/check`}
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
                        >
                            Conferência v2
                        </Link>
                        <Link
                            to={`/shipments/${shipmentId}/conference`}
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
                        >
                            Romaneio v2
                        </Link>
                    </div>
                ) : (
                    <p className="mt-3 text-amber-800">
                        Importe a planilha Details na tarefa de embarque ou na recepção no CD.
                    </p>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <section className="grid grid-cols-3 gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <Pill label="Itens" value={data.totals.total} tone="gray" />
                <Pill label="Alocados" value={data.totals.allocated} tone="green" />
                <Pill label="Pendentes" value={data.totals.pending} tone="amber" />
            </section>

            <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
                <h3 className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-800">
                    Itens do embarque
                </h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-100 text-sm">
                        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                            <tr>
                                <th className="px-3 py-2 text-left">Produto</th>
                                <th className="px-3 py-2 text-left">Série</th>
                                <th className="px-3 py-2 text-left">Status</th>
                                <th className="px-3 py-2 text-left">Localização CD</th>
                                <th className="px-3 py-2 text-left">Chegada</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {data.items.map((it) => (
                                <tr key={it.id}>
                                    <td className="px-3 py-2 text-gray-700">
                                        <div className="font-medium">{it.product_name ?? '—'}</div>
                                        <div className="text-xs text-gray-500">{it.product_code}</div>
                                    </td>
                                    <td className="px-3 py-2 text-gray-700">{it.serial_number ?? '—'}</td>
                                    <td className="px-3 py-2 text-gray-700">{it.status}</td>
                                    <td className="px-3 py-2 text-gray-700">
                                        {it.cd_location ?? <span className="text-amber-600">Não alocado</span>}
                                    </td>
                                    <td className="px-3 py-2 text-gray-700">
                                        {it.cd_arrived_at?.slice(0, 10) ?? '—'}
                                    </td>
                                </tr>
                            ))}
                            {data.items.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                                        Sem itens vinculados a este embarque.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <Text className="text-xs text-gray-500">
                Para alocar itens em ruas/prateleiras ou editar localizações em massa, use “Editar no painel completo (v1)”.
            </Text>
        </div>
    );
}
